import type { MutableRefObject } from "react";
import { toast } from "@volca/ui/lib/toast";
import type {
  AiSpreadsheetValueType,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import type { MutationErrorHandlers } from "./mutation-error-feedback";
import type { ViewerMutations } from "./use-viewer-mutations";
import type { UseWorkbookHistoryResult } from "./use-workbook-history";

interface CreateColumnValueTypeChangeParams {
  runId: string;
  activeSheet: IAiSpreadsheetSheet | undefined;
  pendingWorkbookMutations: number;
  canMutateActiveSheet: boolean;
  isHistoryBlocked: boolean;
  isSavingLayout: boolean;
  history: UseWorkbookHistoryResult;
  workbookVersionRef: MutableRefObject<number | undefined>;
  recordWorkbookVersion: (version: number | undefined) => void;
  handleMutationError: MutationErrorHandlers["handleMutationError"];
  mutations: Pick<
    ViewerMutations,
    | "updateColumnValueTypeAsync"
    | "isUpdatingAnyCell"
    | "isBulkPasting"
    | "isCreatingManualRow"
    | "isUpdatingColumnValueType"
  >;
}

export const createColumnValueTypeChange = ({
  runId,
  activeSheet,
  pendingWorkbookMutations,
  canMutateActiveSheet,
  isHistoryBlocked,
  isSavingLayout,
  history,
  workbookVersionRef,
  recordWorkbookVersion,
  handleMutationError,
  mutations: {
    updateColumnValueTypeAsync,
    isUpdatingAnyCell,
    isBulkPasting,
    isCreatingManualRow,
    isUpdatingColumnValueType,
  },
}: CreateColumnValueTypeChangeParams) => {
  const canChangeColumnValueType =
    pendingWorkbookMutations === 0 &&
    canMutateActiveSheet &&
    !isHistoryBlocked &&
    !isUpdatingAnyCell &&
    !isBulkPasting &&
    !isCreatingManualRow &&
    !isUpdatingColumnValueType &&
    !isSavingLayout;

  const changeColumnValueType = async (
    col: number,
    valueType: AiSpreadsheetValueType,
  ) => {
    if (!activeSheet || !canChangeColumnValueType) return;
    try {
      const result = await updateColumnValueTypeAsync({
        runId,
        sheetId: activeSheet.id,
        col,
        valueType,
        baseVersion: workbookVersionRef.current,
      });
      recordWorkbookVersion(result.version);
      history.reset();
      toast.success("Column value type updated");
    } catch (mutationError) {
      handleMutationError("Failed to change column value type", mutationError);
    }
  };

  return { canChangeColumnValueType, changeColumnValueType };
};
